/**
 * FilterBar
 *
 * Consolidated context + view + filter controls for the transactions
 * table pages (`/transactions`, `/snapshots`). Replaces three previously
 * separate stacked bars (`ContextBar`, `VisualizationBar`, `PivotBar`,
 * ~223-370px combined — see docs/plans/UI_LAYOUT_DENSITY_PLAN.md §H5/H6)
 * with two ~44px rows, because measurement showed those bars only ever
 * used 32-55% of the available width — the problem was never padding, it
 * was three bars stacked vertically when their controls fit in one.
 *
 * Row 1: date range, account selector, an optional `accountAdornment` slot
 * right after it (e.g. Scenario Editor's "ACTIVE · N Transactions" chip),
 * view-mode switch, and a right-aligned `actions` slot (AI Analysis /
 * Export / Reset — supplied by the caller so this bar stays page-agnostic).
 * Row 1b (pivot view only): group-by field + orientation — kept as its own
 * row since it's a materially different control set from row 1, not filler.
 * Row 2: one integrated search control (the "search in: merchant/category"
 * mode switch is the search bar's own left segment, not a separate button —
 * same Menu/handlers underneath, just one visual pill instead of two),
 * flow filter, "filter by", and a right-aligned `secondaryExtra` slot
 * (Scenario Editor's Edited/Original status filter).
 *
 * @fileoverview Consolidated filter bar for the transactions table pages
 */

import React, { type ReactNode } from 'react';
import {
  HStack,
  Flex,
  Text,
  Box,
  Button,
  Icon,
  IconButton,
  Input,
  ButtonGroup,
  Menu,
  MenuButton,
  MenuList,
  Portal,
  MenuItem,
  MenuDivider,
  Spinner,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  MdNavigateBefore,
  MdNavigateNext,
  MdSearch,
  MdExpandMore,
  MdPerson,
  MdCategory,
  MdViewColumn,
  MdViewStream,
  MdViewList,
  MdGridOn,
  MdShowChart,
  MdAccountBalance,
} from 'react-icons/md';
import { MonthYearPicker } from '../dashboard/MonthYearPicker';
import { AccountSelector } from '../common/AccountSelector';

// ============================================================================
// Types (unchanged from the bars this replaces, so callers/hooks that
// imported them from this module — e.g. usePivotState — keep working)
// ============================================================================

export type ViewMode = 'transactions' | 'pivot' | 'flow' | 'balance';

export type PivotMode =
  | 'vendor_month'
  | 'vendor_year'
  | 'merchant_month'
  | 'merchant_year'
  | 'category_month'
  | 'category_year'
  | 'subcategory_month'
  | 'subcategory_year'
  | 'total_month'
  | 'total_year';

export type SearchMode = 'merchant' | 'category';
export type FilterMode = 'none' | 'checks' | 'zelle' | 'transfer';
export type FlowFilter = 'all' | 'income' | 'expense';

const PIVOT_GROUPS: { group: string; options: { label: string; value: PivotMode }[] }[] = [
  {
    group: 'Vendor',
    options: [
      { label: 'Vendor by Month', value: 'vendor_month' },
      { label: 'Vendor by Year', value: 'vendor_year' },
    ],
  },
  {
    group: 'Merchant',
    options: [
      { label: 'Merchant by Month', value: 'merchant_month' },
      { label: 'Merchant by Year', value: 'merchant_year' },
    ],
  },
  {
    group: 'Category',
    options: [
      { label: 'Category by Month', value: 'category_month' },
      { label: 'Category by Year', value: 'category_year' },
    ],
  },
  {
    group: 'Subcategory',
    options: [
      { label: 'Subcategory by Month', value: 'subcategory_month' },
      { label: 'Subcategory by Year', value: 'subcategory_year' },
    ],
  },
  {
    group: 'Total',
    options: [
      { label: 'Total by Month', value: 'total_month' },
      { label: 'Total by Year', value: 'total_year' },
    ],
  },
];

const ALL_PIVOT_OPTIONS = PIVOT_GROUPS.flatMap((g) => g.options);

const getPivotLabel = (mode: PivotMode): string =>
  ALL_PIVOT_OPTIONS.find((o) => o.value === mode)?.label ?? 'Vendor by Month';

const VIEW_ICONS: Record<ViewMode, React.ElementType> = {
  transactions: MdViewList,
  pivot: MdGridOn,
  flow: MdShowChart,
  balance: MdAccountBalance,
};

const VIEW_LABELS: Record<ViewMode, string> = {
  transactions: 'Transactions',
  pivot: 'Group by',
  flow: 'Flow',
  balance: 'Balance',
};

// ============================================================================
// Props
// ============================================================================

export interface FilterBarProps {
  // Row 1 — context
  startDate: string;
  endDate: string;
  onRangeSelect: (start: string, end: string) => void;
  onPreviousPeriod: () => void;
  onNextPeriod: () => void;
  selectedAccountIds: string[];
  onAccountsChange: (ids: string[]) => void;
  isAccountSelectionLoading?: boolean;
  /** Rendered immediately after the account selector — e.g. Scenario Editor's Active/N Transactions chip. */
  accountAdornment?: ReactNode;

  // Row 1 — view mode
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;

  // Row 1b — pivot configuration (rendered only while viewMode === 'pivot')
  pivotMode: PivotMode;
  onPivotModeChange: (mode: PivotMode) => void;
  pivotOrientation: 'vertical' | 'horizontal';
  onPivotOrientationChange: (orientation: 'vertical' | 'horizontal') => void;

  // Row 2 — search & filters
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  searchMode: SearchMode;
  onSearchModeChange: (mode: SearchMode) => void;
  filterMode: FilterMode;
  onFilterModeChange: (mode: FilterMode) => void;
  flowFilter: FlowFilter;
  onFlowFilterChange: (filter: FlowFilter) => void;

  /** Right side of row 1 — AI Analysis / Export / Reset / status chips, page-specific. */
  actions?: ReactNode;
  /** Right side of row 2 — Scenario Editor's Edited/Original status filter. */
  secondaryExtra?: ReactNode;
}

// ============================================================================
// Component
// ============================================================================

export const FilterBar: React.FC<FilterBarProps> = ({
  startDate,
  endDate,
  onRangeSelect,
  onPreviousPeriod,
  onNextPeriod,
  selectedAccountIds,
  onAccountsChange,
  isAccountSelectionLoading = false,
  accountAdornment,
  viewMode,
  onViewModeChange,
  pivotMode,
  onPivotModeChange,
  pivotOrientation,
  onPivotOrientationChange,
  searchQuery,
  onSearchQueryChange,
  searchMode,
  onSearchModeChange,
  filterMode,
  onFilterModeChange,
  flowFilter,
  onFlowFilterChange,
  actions,
  secondaryExtra,
}) => {
  const bgColor = useColorModeValue('gray.50', 'bg.surface');
  const borderColor = useColorModeValue('gray.200', 'xacto.borderGray');
  const textColor = useColorModeValue('gray.900', 'gray.100');
  const inputBg = useColorModeValue('white', 'bg.primary');
  const hoverBg = useColorModeValue('gray.100', 'bg.primary');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const accentColor = '#9333EA';

  const activeBtn = (active: boolean) => ({
    bg: active ? accentColor : 'transparent',
    color: active ? 'white' : textColor,
    border: `1px solid ${borderColor}`,
    _hover: { bg: active ? accentColor : hoverBg },
  });

  return (
    <Box bg={bgColor} borderBottomWidth="1px" borderBottomColor={borderColor} flexShrink={0}>
      {/* Row 1: date range, accounts, view mode, page actions */}
      {/*
        Every row wraps and uses minH (not a fixed h). A fixed-height,
        non-wrapping row looks tidy at 1920 but silently pushes whatever
        doesn't fit past the right edge, where the page's `overflow:
        hidden` (needed for the contained-scroll table) clips it — at
        1440 that made Export unreachable, at 1024 the whole view-mode
        switcher. Wrapping costs a little height on narrow screens and
        nothing at all on wide ones, and no control can ever become
        unclickable.
      */}
      <HStack
        minH="44px"
        px={4}
        py={1}
        spacing={3}
        rowGap={2}
        flexWrap="wrap"
        borderBottomWidth="1px"
        borderBottomColor={borderColor}
      >
        <MonthYearPicker startDate={startDate} endDate={endDate} onRangeSelect={onRangeSelect} />

        <HStack spacing={0}>
          <IconButton
            aria-label="Previous period"
            icon={<MdNavigateBefore />}
            variant="ghost"
            size="sm"
            onClick={onPreviousPeriod}
          />
          <IconButton
            aria-label="Next period"
            icon={<MdNavigateNext />}
            variant="ghost"
            size="sm"
            onClick={onNextPeriod}
          />
        </HStack>

        <Box minW="180px" maxW="240px">
          <HStack spacing={2} align="center">
            <Box flex="1">
              <AccountSelector
                values={selectedAccountIds}
                onChangeMulti={onAccountsChange}
                multiSelect
                compact
                size="sm"
              />
            </Box>
            {isAccountSelectionLoading && <Spinner size="sm" color="accent.primary" />}
          </HStack>
        </Box>

        {accountAdornment}

        <Box w="1px" h="24px" bg={borderColor} flexShrink={0} />

        <ButtonGroup size="sm" isAttached variant="outline" flexShrink={0}>
          {(Object.keys(VIEW_LABELS) as ViewMode[]).map((mode) => (
            <Button
              key={mode}
              leftIcon={<Icon as={VIEW_ICONS[mode]} boxSize={3.5} />}
              fontSize="xs"
              fontWeight="500"
              {...activeBtn(viewMode === mode)}
              onClick={() => onViewModeChange(mode)}
            >
              {VIEW_LABELS[mode]}
            </Button>
          ))}
        </ButtonGroup>

        <Box flex={1} />

        {actions}
      </HStack>

      {/* Row 1b: pivot configuration — only while grouping */}
      {viewMode === 'pivot' && (
        <HStack
          minH="40px"
          px={4}
          py={1}
          spacing={3}
          rowGap={2}
          flexWrap="wrap"
          borderBottomWidth="1px"
          borderBottomColor={borderColor}
        >
          <Text fontSize="xs" fontWeight="600" color={mutedColor} flexShrink={0}>
            Group by
          </Text>
          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<Icon as={MdExpandMore} />}
              variant="outline"
              size="sm"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
            >
              {getPivotLabel(pivotMode)}
            </MenuButton>
            {/*
              Portal + maxH: this bar lives inside the dense pages' bounded,
              `overflow: hidden` layout (AppShell fullHeight), which clips a
              non-portaled dropdown — measured at 1440x620 only 11 of these
              15 options were reachable, at 1280x520 only 9. Portaling to
              document.body escapes the clip (same reason AccountSelector
              does it), and maxH keeps a 15-item menu scrollable rather
              than merely overflowing on short screens.
            */}
            <Portal>
              <MenuList zIndex={1400} maxH="min(50vh, 420px)" overflowY="auto">
                {PIVOT_GROUPS.map((group, gi) => (
                <React.Fragment key={group.group}>
                  {gi > 0 && <MenuDivider />}
                  <MenuItem
                    isDisabled
                    fontSize="xs"
                    color="gray.500"
                    fontWeight="700"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    cursor="default"
                    _disabled={{ opacity: 1, cursor: 'default' }}
                  >
                    {group.group}
                  </MenuItem>
                  {group.options.map((opt) => (
                    <MenuItem
                      key={opt.value}
                      pl={6}
                      fontWeight={pivotMode === opt.value ? '600' : '400'}
                      color={pivotMode === opt.value ? accentColor : textColor}
                      onClick={() => onPivotModeChange(opt.value)}
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                </React.Fragment>
                ))}
              </MenuList>
            </Portal>
          </Menu>

          <Box flex={1} />

          <Text fontSize="xs" fontWeight="600" color={mutedColor} flexShrink={0}>
            View
          </Text>
          <ButtonGroup size="sm" isAttached variant="outline">
            <Button
              leftIcon={<Icon as={MdViewStream} boxSize={3.5} />}
              fontSize="xs"
              fontWeight="500"
              {...activeBtn(pivotOrientation === 'vertical')}
              onClick={() => onPivotOrientationChange('vertical')}
              aria-label="Vertical grouping"
            >
              Vertical
            </Button>
            <Button
              leftIcon={<Icon as={MdViewColumn} boxSize={3.5} />}
              fontSize="xs"
              fontWeight="500"
              {...activeBtn(pivotOrientation === 'horizontal')}
              onClick={() => onPivotOrientationChange('horizontal')}
              aria-label="Horizontal grouping"
            >
              Horizontal
            </Button>
          </ButtonGroup>
        </HStack>
      )}

      {/* Row 2: search, flow, filters, optional status filter */}
      <HStack minH="44px" px={4} py={1} spacing={3} rowGap={2} flexWrap="wrap">
        {/*
          One integrated control instead of a separate "search in" menu
          button sitting next to a plain search box: the mode switcher is
          the search bar's own left segment, sharing its border/height/
          background as a single pill. Still the exact same Menu with the
          same two items and handlers underneath — only the visual
          grouping changed, not the behavior.
        */}
        <Flex
          align="center"
          bg={inputBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          h="32px"
          w="280px"
          flexShrink={0}
          overflow="hidden"
        >
          <Menu>
            <MenuButton
              as={Button}
              variant="ghost"
              size="sm"
              h="full"
              px={2}
              borderRadius={0}
              borderRight="1px solid"
              borderColor={borderColor}
              flexShrink={0}
              leftIcon={<Icon as={searchMode === 'merchant' ? MdPerson : MdCategory} boxSize={3.5} />}
              rightIcon={<Icon as={MdExpandMore} boxSize={3} />}
              fontSize="xs"
              fontWeight="500"
              color={textColor}
              aria-label={`Search in: ${searchMode === 'merchant' ? 'Merchant' : 'Category'}. Click to change.`}
            >
              {searchMode === 'merchant' ? 'Merchant' : 'Category'}
            </MenuButton>
            {/* Portaled — see the Group by menu above for why. */}
            <Portal>
              <MenuList zIndex={1400}>
                <MenuItem
                  icon={<Icon as={MdPerson} />}
                  onClick={() => onSearchModeChange('merchant')}
                  fontWeight={searchMode === 'merchant' ? '600' : '400'}
                  color={searchMode === 'merchant' ? accentColor : textColor}
                >
                  Merchant
                </MenuItem>
                <MenuItem
                  icon={<Icon as={MdCategory} />}
                  onClick={() => onSearchModeChange('category')}
                  fontWeight={searchMode === 'category' ? '600' : '400'}
                  color={searchMode === 'category' ? accentColor : textColor}
                >
                  Category
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>

          <Icon as={MdSearch} color="gray.400" boxSize={4} ml={2} flexShrink={0} />
          <Input
            variant="unstyled"
            placeholder={`Search by ${searchMode}...`}
            aria-label={`Search transactions by ${searchMode}`}
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            fontSize="sm"
            px={2}
            h="full"
          />
        </Flex>

        <Box w="1px" h="24px" bg={borderColor} flexShrink={0} display={{ base: 'none', md: 'block' }} />

        <ButtonGroup size="sm" isAttached variant="outline" flexShrink={0}>
          <Button fontSize="xs" fontWeight="500" {...activeBtn(flowFilter === 'all')} onClick={() => onFlowFilterChange('all')}>
            All
          </Button>
          <Button fontSize="xs" fontWeight="500" {...activeBtn(flowFilter === 'income')} onClick={() => onFlowFilterChange('income')}>
            Income
          </Button>
          <Button fontSize="xs" fontWeight="500" {...activeBtn(flowFilter === 'expense')} onClick={() => onFlowFilterChange('expense')}>
            Expense
          </Button>
        </ButtonGroup>

        <ButtonGroup size="sm" isAttached variant="outline" flexShrink={0}>
          <Button fontSize="xs" fontWeight="500" {...activeBtn(filterMode === 'none')} onClick={() => onFilterModeChange('none')}>
            None
          </Button>
          <Button fontSize="xs" fontWeight="500" {...activeBtn(filterMode === 'checks')} onClick={() => onFilterModeChange('checks')}>
            Checks
          </Button>
          <Button fontSize="xs" fontWeight="500" {...activeBtn(filterMode === 'zelle')} onClick={() => onFilterModeChange('zelle')}>
            Zelle
          </Button>
          <Button fontSize="xs" fontWeight="500" {...activeBtn(filterMode === 'transfer')} onClick={() => onFilterModeChange('transfer')}>
            Transfers
          </Button>
        </ButtonGroup>

        <Box flex={1} display={{ base: 'none', xl: 'block' }} />

        {secondaryExtra}
      </HStack>
    </Box>
  );
};

export default FilterBar;
